import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { type Observable, catchError, map, throwError } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CmsService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches content from Builder.io CMS by model name and page name.
   * @param model - The Builder.io model name (e.g., 'landing-page', 'header')
   * @param pageName - The name of the content page to retrieve from the model
   * @param isEnriched - Optional flag to enrich the response with resolved references (default: true)
   * @returns Observable<any> - Observable containing the flattened content data object, or an error if content is not found
   */
  getContent(model: string, pageName: string, isEnriched = true): Observable<any> {
    const url =
      environment.contentAPIUrl +
      encodeURIComponent(model) +
      '?apiKey=' +
      environment.builderPublicKey +
      '&query.name=' +
      encodeURIComponent(pageName) +
      (isEnriched ? '&enrich=true' : '');
    const res = this.http.get(url);

    return res.pipe(
      map((data: any) => {
        if (data.results.length > 0 && data.results[0].data !== null) {
          return data.results[0].data;
        } else {
          return throwError(() => new Error(`CMS error: Failed to get content for ${model}:${pageName}.`));
        }
      }),
    );
  }

  /**
   * Fetches blog posts from the Builder.io CMS, limited by count and sorted by date descending.
   * @param blogCount - The maximum number of blog posts to retrieve
   * @returns Observable<any> - Observable containing an array of blog result objects with url, thumbNail, date, and excerpt fields
   */
  getBlogs(blogCount: number, useResidentialCategoryFilter = false): Observable<any> {
    const blogModel = encodeURIComponent('gs-blog-detail-page');
    const blogFields = 'name,data.url,data.thumbNail,data.date,data.excerpt';
    const categoryExclusionQuery = encodeURIComponent(
      JSON.stringify({
        'data.categories': {
          $nin: ['Natural Gas Industry'],
        },
      }),
    );
    const url =
      environment.contentAPIUrl +
      blogModel +
      '?apiKey=' +
      environment.digitalProductsBuilderKey +
      '&fields=' +
      blogFields +
      (useResidentialCategoryFilter ? '&query=' + categoryExclusionQuery : '') +
      '&sort.data.date=-1&offset=0&limit=' +
      blogCount;

    const res = this.http.get(url);

    return res.pipe(
      map((data: any) => {
        if (data.results.length > 0 && data.results[0].data !== null) {
          return data.results;
        } else {
          return throwError(() => new Error('Blogs CMS error: Failed to get content for gs-blog-detail-page.'));
        }
      }),
    );
  }

  /**
   * Fetches footer category links from the Builder.io CMS.
   * @returns Observable<any> - Observable containing an array of footer category data objects with enriched references
   */
  getFooterCategory(): Observable<any> {
    const footerCategoryModelUrl =
      environment.contentAPIUrl +
      encodeURIComponent('footer-link-category') +
      '?apiKey=' +
      environment.digitalProductsBuilderKey +
      '&fields=data&enrich=true&limit=10';
    return this.http.get(footerCategoryModelUrl).pipe(
      catchError((error) => {
        console.error('Error in getFooterCategory:', error);
        return throwError(() => new Error('FooterCategory Observable Failed'));
      }),
    );
  }

  /**
   * Fetches footer links from the Builder.io CMS.
   * @returns Observable<any> - Observable containing an array of footer link data objects with enriched references
   */
  getFooterLinks(): Observable<any> {
    const footerLinkModelUrl =
      environment.contentAPIUrl +
      encodeURIComponent('footer-link-model') +
      '?apiKey=' +
      environment.digitalProductsBuilderKey +
      '&fields=data&enrich=true&limit=100';
    return this.http.get(footerLinkModelUrl).pipe(
      catchError((error) => {
        console.error('Error in getFooterLinks:', error);
        return throwError(() => new Error('FooterLinks Observable Failed'));
      }),
    );
  }

  /**
   * Extracts and parses the data object from a Builder.io reference.
   * @param refData - A Builder.io reference object containing a value.data property
   * @returns any | null - A deep copy of the reference data, or null if the reference data is not found
   */
  getDataFromBuilderReference(refData: any): any | null {
    if (refData.value?.data) {
      return JSON.parse(JSON.stringify(refData.value.data));
    } else {
      return null;
    }
  }

  /**
   * Recursively flattens all Builder.io references and localized values while preserving object keys.
   * Converts @builder.io/core:Reference objects to their data content, preserves the builderRefName,
   * and extracts Default values from @builder.io/core:LocalizedValue objects.
   * @param obj - A Builder.io content object that may contain nested references or localized values
   * @returns any - A flattened version of the object with all references and localized values resolved
   */
  flattenBuilderReferencesPreservingKeys(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.flattenBuilderReferencesPreservingKeys(item));
    }

    if (obj && typeof obj === 'object' && obj['@type'] === '@builder.io/core:Reference' && obj.value?.data) {
      const flattenedData = this.flattenBuilderReferencesPreservingKeys(obj.value.data);
      const builderRefName = obj.value.name;

      if (flattenedData && typeof flattenedData === 'object' && !Array.isArray(flattenedData)) {
        return {
          ...flattenedData,
          builderRefName: builderRefName ?? flattenedData.builderRefName,
        };
      }

      return flattenedData;
    }

    if (
      obj &&
      typeof obj === 'object' &&
      obj['@type'] === '@builder.io/core:LocalizedValue' &&
      obj.Default !== undefined
    ) {
      return this.flattenBuilderReferencesPreservingKeys(obj.Default);
    }

    if (obj && typeof obj === 'object') {
      const result: Record<string, any> = {};
      for (const key in obj) {
        result[key] = this.flattenBuilderReferencesPreservingKeys(obj[key]);
      }
      return result;
    }

    return obj;
  }
}
